import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { HelpHint } from "@/components/HelpHint";
import { Badge } from "@/components/ui/badge";
import { isDemoUser, simulateDemoAction } from "@/lib/demoMode";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate } from "@/lib/helpers";
import { Receipt, CheckCircle2, XCircle, Wallet, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "fuel", label: "Fuel" },
  { value: "car_wash", label: "Car Wash" },
  { value: "oil_change", label: "Oil Change" },
  { value: "tires", label: "Tires" },
  { value: "parking", label: "Parking" },
  { value: "emergency_supplies", label: "Emergency Supplies" },
  { value: "other", label: "Other" },
];

const emptyForm = { category: "fuel", amount: "", date: new Date().toISOString().slice(0, 10), notes: "", receipt_photo: null };

export default function Expenses() {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "office_staff";
  const isInstructor = user?.role === "instructor";
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState("all");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/expenses");
      setExpenses(res.data);
    } catch {
      toast.error("Could not load expenses");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, receipt_photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.category || !form.amount || !form.date) {
      toast.error("Category, amount, and date are required");
      return;
    }
    if (isDemoUser(user)) {
      const preview = {
        id: `demo-${Date.now()}`, user_id: user.id, submitted_by_name: user.full_name,
        category: form.category, amount: parseFloat(form.amount), date: form.date,
        notes: form.notes, receipt_url: form.receipt_photo, status: "pending",
        submitted_at: new Date().toISOString(),
      };
      setExpenses((prev) => [preview, ...prev]);
      simulateDemoAction("this receipt would be submitted and the office notified for review.");
      setForm(emptyForm);
      return;
    }
    try {
      await api.post("/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Receipt submitted — office will review it shortly.");
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit expense");
    }
  };

  const updateStatus = async (id, status, review_notes) => {
    try {
      await api.put(`/expenses/${id}/status`, { status, review_notes });
      toast.success(status === "approved" ? "Expense approved" : status === "reimbursed" ? "Marked as reimbursed" : "Expense rejected");
      setRejectTarget(null);
      setRejectNotes("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update expense");
    }
  };

  const filtered = isStaff && filter !== "all" ? expenses.filter((e) => e.status === filter) : expenses;

  if (loading) return <Skeleton className="h-64 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {isInstructor && (
        <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3" data-testid="expense-submission-form">
          <div className="flex items-center gap-1.5">
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Receipt className="h-5 w-5 text-[hsl(var(--role-accent))]" /> Submit an Expense</h2>
            <HelpHint text="Upload a receipt for reimbursement. Office will review and approve it." testId="expense-submit-help" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="expense-category-select">
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <Input type="number" step="0.01" min="0" className="pl-7" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="expense-amount-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="expense-date-picker" />
            </div>
            <div className="space-y-1.5">
              <Label>Receipt photo (optional)</Label>
              <Input type="file" accept="image/*" onChange={handleReceiptChange} data-testid="expense-receipt-upload" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="expense-notes-textarea" />
            </div>
          </div>
          {form.receipt_photo && (
            <img src={form.receipt_photo} alt="receipt preview" className="h-20 rounded border object-cover" />
          )}
          <Button onClick={submit} className="bg-[hsl(var(--role-accent))] hover:opacity-95" data-testid="expense-submit-button">Submit Expense</Button>
        </div>
      )}

      {isStaff && (
        <div className="flex flex-wrap items-center gap-2" data-testid="expense-approval-queue">
          {["all", "pending", "approved", "rejected", "reimbursed"].map((f) => (
            <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} className={filter === f ? "bg-[hsl(var(--role-accent))] hover:opacity-95" : ""} onClick={() => setFilter(f)} data-testid={`expense-filter-${f}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      )}

      <div className="rounded-[var(--radius)] border bg-card overflow-hidden overflow-x-auto" data-testid="expense-status-list">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground" data-testid="expenses-empty-state">No expenses to show yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {isStaff && <TableHead>Instructor</TableHead>}
                <TableHead>Category</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead className="hidden sm:table-cell">Date</TableHead>
                <TableHead>Status</TableHead>
                {isStaff && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid="expense-status-row">
                  {isStaff && <TableCell className="font-medium">{e.submitted_by_name}</TableCell>}
                  <TableCell className="capitalize">{e.category.replace(/_/g, " ")}</TableCell>
                  <TableCell className="font-mono-ui">${e.amount.toFixed(2)}</TableCell>
                  <TableCell className="hidden sm:table-cell text-muted-foreground">{formatDate(e.date)}</TableCell>
                  <TableCell><StatusBadge status={e.status} /></TableCell>
                  {isStaff && (
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {e.receipt_url && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${e.receipt_url}`} target="_blank" rel="noreferrer" data-testid="expense-receipt-link">
                            <Button size="icon" variant="ghost" className="h-8 w-8"><ImageIcon className="h-4 w-4" /></Button>
                          </a>
                        )}
                        {e.status === "pending" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => updateStatus(e.id, "approved")} data-testid="expense-approve-button">
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => setRejectTarget(e)} data-testid="expense-reject-button">
                              <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        {e.status === "approved" && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(e.id, "reimbursed")} data-testid="expense-mark-reimbursed-button">
                            <Wallet className="h-3.5 w-3.5 mr-1" /> Mark Reimbursed
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent data-testid="expense-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason (optional)</Label>
            <Textarea value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} placeholder="Let the instructor know why this was rejected" data-testid="expense-reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="destructive" onClick={() => updateStatus(rejectTarget.id, "rejected", rejectNotes)} data-testid="expense-reject-confirm-button">Reject Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
